import sourceTemplates from './documentTemplateSources.json';

const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatAddress = (address) => {
    if (!address) return '';
    return [
        address.calle,
        address.numero_exterior,
        address.numero_interior,
        address.colonia,
        address.ciudad,
        address.estado,
        address.codigo_postal
    ].filter(Boolean).join(', ');
};

const getCompanyAddress = (company) => {
    const address = company?.addresses?.find((entry) => entry.is_fiscal) || company?.addresses?.[0];
    return formatAddress(address);
};

const getWorkAddress = (document, data) => {
    return formatAddress(document) || formatAddress(data) || data.lugar_trabajo || data.domicilio_patron || '';
};

const getFullName = (person) => [person?.nombre, person?.apellidos].filter(Boolean).join(' ').trim();

const scheduleGridToken = '__CONTRACT_SCHEDULE_GRID__';
const additionalBenefitsGridToken = '__CONTRACT_ADDITIONAL_BENEFITS_GRID__';
const removeMarker = Symbol('remove-marker');

const parseArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string') return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const parseObject = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) return value;
    if (typeof value !== 'string') return {};
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const additionalBenefitsRows = (document, additional) => {
    const rows = parseArray(document.dynamic_data || additional.dynamic_data || document.conceptos_adicionales);
    return rows.filter((row) => row && (row.concepto || row.base || row.unidad));
};

const scheduleTableRows = (schedule = {}) => {
    const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const hours = (periods = {}) => [
        periods.inicio && periods.fin ? `${periods.inicio} a ${periods.fin}` : '',
        periods.inicio2 && periods.fin2 ? `${periods.inicio2} a ${periods.fin2}` : ''
    ].filter(Boolean).join(' y ');
    const breakTime = schedule.tiempoDescanso
        ? `${schedule.tiempoDescanso} minutos`
        : 'No especificado';

    if (Array.isArray(schedule.horarioVariable)) {
        return schedule.horarioVariable
            .filter((entry) => entry.dia)
            .map((entry) => [entry.dia, hours(entry.horas), breakTime]);
    }

    const fixed = schedule.horarioFijo;
    if (!fixed?.desdeDia || !fixed?.hastaDia) return [];
    const start = days.findIndex((day) => normalize(day) === normalize(fixed.desdeDia));
    const end = days.findIndex((day) => normalize(day) === normalize(fixed.hastaDia));
    if (start < 0 || end < 0) return [];

    const workingDays = [];
    for (let index = start, count = 0; count < days.length; count += 1) {
        workingDays.push(days[index]);
        if (index === end) break;
        index = (index + 1) % days.length;
    }
    const dailyHours = hours(fixed.horas);
    return workingDays.map((day) => [day, dailyHours, breakTime]);
};

const scheduleTypes = {
    fijoDiasFijos: 'Horario fijo en días fijos',
    variableDiasFijos: 'Horario variable en días fijos',
    fijoDescansoVariable: 'Horario fijo con días de descanso variables',
    variableDescansoVariable: 'Horario variable con días de descanso variables'
};

const scheduleShiftLabels = {
    diurno: 'Diurno',
    nocturno: 'Nocturno',
    mixto: 'Mixto'
};

const renderScheduleGrid = (schedule) => {
    const rows = scheduleTableRows(schedule);
    if (!rows.length) return '';
    const summary = [
        ['Turno', scheduleShiftLabels[schedule.turno] || schedule.turno],
        ['Tipo de horario', scheduleTypes[schedule.tipoHorario]],
        ['Descanso semanal', [
            schedule.diasDescanso !== undefined && schedule.diasDescanso !== null && schedule.diasDescanso !== ''
                ? `${schedule.diasDescanso} ${Number(schedule.diasDescanso) === 1 ? 'día' : 'días'}`
                : '',
            weeklyRestDays(schedule)
        ].filter(Boolean).join(': ')],
        ['Tiempo de descanso diario', schedule.tiempoDescanso
            ? `${schedule.tiempoDescanso} minutos`
            : 'No especificado']
    ].filter(([, value]) => value);
    const summaryGrid = summary.length
        ? `<div class="contract-schedule-summary">${summary.map(([label, value]) =>
            `<div class="contract-schedule-summary-label">${escapeHtml(label)}</div><div class="contract-schedule-summary-value">${escapeHtml(value)}</div>`
        ).join('')}</div>`
        : '';
    const header = ['Día de labores', 'Horario diario', 'Tiempo de descanso']
        .map((value) => `<div class="contract-grid-cell contract-grid-heading">${value}</div>`)
        .join('');
    const body = rows.map((row) => `<div class="contract-grid-row">${row
        .map((value) => `<div class="contract-grid-cell">${escapeHtml(value)}</div>`)
        .join('')}</div>`).join('');
    return `${summaryGrid}<div class="contract-data-grid contract-schedule-grid"><div class="contract-grid-row">${header}</div>${body}</div>`;
};

const renderAdditionalBenefitsGrid = (document, additional) => {
    const benefits = additionalBenefitsRows(document, additional);
    if (!benefits.length) return '';
    const header = ['Concepto', 'Base', 'Unidad']
        .map((value) => `<div class="contract-grid-cell contract-grid-heading">${value}</div>`)
        .join('');
    const rows = benefits.map((benefit) => {
        const unit = { dias: 'Días', porciento: 'Porcentaje', pesos: 'Pesos' }[benefit.unidad] || benefit.unidad || '';
        return `<div class="contract-grid-row">${[benefit.concepto, benefit.base, unit]
            .map((value) => `<div class="contract-grid-cell">${escapeHtml(value || '')}</div>`)
            .join('')}</div>`;
    }).join('');
    return `<div class="contract-data-grid contract-benefits-grid"><div class="contract-grid-row">${header}</div>${rows}</div>`;
};

const vacationSchedule = (value) => {
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return [];
        }
    }
    if (!value || typeof value !== 'object') return [];

    const baseDays = Number(value.first_year);
    if (!Number.isFinite(baseDays) || baseDays <= 0) return [];

    const rows = [{ year: 1, days: baseDays }];
    let boundaryYear = 1;
    let currentDays = baseDays;
    let recurringRule = null;

    (Array.isArray(value.ranges) ? value.ranges : []).forEach((range) => {
        if (recurringRule || !range) return;
        const step = Number(range.step);
        const increase = Number(range.days);
        if (!Number.isInteger(step) || step <= 0 || !Number.isFinite(increase) || increase <= 0) return;

        const hasLimit = range.limit !== '' && range.limit !== undefined && range.limit !== null;
        const limit = hasLimit ? Number(range.limit) : null;
        if (hasLimit && (!Number.isInteger(limit) || limit <= boundaryYear)) return;

        const firstIncreaseYear = boundaryYear + step;
        const lastYear = hasLimit ? limit : firstIncreaseYear;
        for (let year = boundaryYear + 1; year <= lastYear; year += 1) {
            if ((year - boundaryYear) % step === 0) currentDays += increase;
            rows.push({ year, days: currentDays });
        }

        if (hasLimit) {
            boundaryYear = limit;
        } else {
            recurringRule = {
                year: firstIncreaseYear + step,
                days: currentDays + increase,
                step,
                increase
            };
        }
    });

    if (recurringRule) {
        rows.push({
            year: `A partir del año ${recurringRule.year}, cada ${recurringRule.step} años`,
            days: `${recurringRule.days} días; aumenta ${recurringRule.increase} días por periodo`
        });
    }
    return rows;
};

const renderVacationTable = (value) => {
    const rows = vacationSchedule(value);
    if (!rows.length) return '';
    const header = ['Año', 'Días de descanso']
        .map((label) => `<div class="contract-grid-cell contract-grid-heading">${label}</div>`)
        .join('');
    const body = rows.map(({ year, days }) =>
        `<div class="contract-grid-row">${[year, days]
            .map((cell) => `<div class="contract-grid-cell">${escapeHtml(cell)}</div>`)
            .join('')}</div>`
    ).join('');
    return `<div class="contract-data-grid contract-vacation-grid"><div class="contract-grid-row">${header}</div>${body}</div>`;
};

const formatSchedule = (schedule = {}) => {
    const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const parts = [];
    const hours = (periods = {}) => [
        periods.inicio && periods.fin ? `${periods.inicio} a ${periods.fin}` : '',
        periods.inicio2 && periods.fin2 ? `${periods.inicio2} a ${periods.fin2}` : ''
    ].filter(Boolean).join(' y ');

    if (schedule.turno) parts.push(`turno ${schedule.turno}`);
    if (scheduleTypes[schedule.tipoHorario]) parts.push(scheduleTypes[schedule.tipoHorario].toLowerCase());
    if (schedule.tipoJornada) parts.push(`jornada ${schedule.tipoJornada}`);
    if (schedule.horarioFijo) {
        const { desdeDia, hastaDia, horas: periods } = schedule.horarioFijo;
        if (desdeDia && hastaDia) {
            const start = days.indexOf(desdeDia);
            const end = days.indexOf(hastaDia);
            const range = [];
            if (start >= 0 && end >= 0) {
                for (let index = start, count = 0; count < days.length; count += 1) {
                    range.push(days[index]);
                    if (index === end) break;
                    index = (index + 1) % days.length;
                }
            }
            if (range.length) parts.push(`días ${range.join(', ')}`);
        }
        const periodText = hours(periods);
        if (periodText) parts.push(periodText);
    }
    if (Array.isArray(schedule.horarioVariable)) {
        const variable = schedule.horarioVariable
            .filter((entry) => entry.dia)
            .map((entry) => `${entry.dia}: ${hours(entry.horas)}`)
            .filter((entry) => !entry.endsWith(': '));
        if (variable.length) parts.push(variable.join('; '));
    }
    if (schedule.tiempoDescanso) parts.push(`${schedule.tiempoDescanso} minutos de descanso`);
    if (schedule.diasDescanso !== undefined && schedule.diasDescanso !== null && schedule.diasDescanso !== '') {
        parts.push(`${schedule.diasDescanso} ${Number(schedule.diasDescanso) === 1 ? 'día' : 'días'} de descanso semanal`);
    }
    return parts.join(', ');
};

const contractSources = {
    obra: 'POR OBRA DETERMINADA',
    determinado: 'POR TIEMPO DETERMINADO',
    indeterminado_capacitacion: 'POR TIEMPO INDETERMINADO SUJETO A CAPACITACION INICIAL',
    indeterminado_prueba: 'POR TIEMPO INDETERMINADO SUJETO A PRUEBA',
    indeterminado_temporada: 'POR TIEMPO INDETERMINADO SUJETO A TEMPORADA',
    indeterminado: 'POR TIEMPO INDETERMINADO'
};

const legalSources = {
    reglamento: 'PARA LA CREACIONDEL RIT',
    utilidades: 'QUE DETERMINA LA PTU',
    declaracion: 'EXHIBICION Y ENTREGA DE DECLARACION ANUAL',
    proyecto: 'ACTA DE PROYECTO DE REPARTO DE UTILIDADES',
    seguridad: 'SEGURIDAD E HIGIENE EN EL TRABAJO',
    capacitacion: 'CAPACITACION, PRODUCTIVIDAD Y ADIESTRAMIENTO',
    antiguedad: 'PARA DETERMINAR ANTIGUEDADES',
    voluntaria: 'RENUNCIA VOLUNTARIA.DOCX',
    embarazo: 'RENUNCIA VOLUNTARIA MUJER EMBARAZADA',
    convenio: 'MODELO DE CONVENIO DE TERMINACION',
    finiquito: 'RECIBO FINIQUITO',
    programacion: 'CONSTANCIA DE ANTIGUEDAD Y PROGRAMACION DE VACACIONES',
    disfrute: 'CONSTANCIA DE DISFRUTE DE VACACIONES',
    diferir: 'SOLICITUD DE DIFERIMIENTO DE PROGRAMACION DE VACACIONES',
    paternidad: 'CONSTANCIA DE GOCE PATERNIDAD',
    anticipo: 'FORMATO DE ANTICIPO DE SALARIO'
};

const findSourceTemplate = (document) => {
    const contract = !document.isLegalDocument;
    const sourceName = (contract ? contractSources : legalSources)[document.tipo];
    if (!sourceName) return null;
    const normalizedName = normalize(sourceName);
    const matches = sourceTemplates.filter((source) => {
        const folder = normalize(source.folder);
        const name = normalize(source.name);
        const isContractFolder = folder === 'CONTRATOS';
        const isLegalFolder = folder !== 'CONTRATOS';
        return (contract ? isContractFolder : isLegalFolder) && name.includes(normalizedName);
    });
    return matches.find((source) => normalize(source.name).replace(/\.DOCX$/, '').endsWith(normalizedName))
        || matches[0]
        || null;
};

const getDataValue = (data, keys) => {
    if (!data || typeof data !== 'object') return '';
    for (const key of keys) {
        const value = data[key];
        if (value !== undefined && value !== null && value !== '') return value;
    }
    return '';
};

const formatPaymentDays = (value) => {
    let items = value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) items = parsed;
        } catch {
            return value;
        }
    }
    if (!Array.isArray(items)) return items;
    const days = items
        .filter((item) => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean);
    if (!days.length) return '';
    const list = days.length < 3
        ? days.join(' y ')
        : `${days.slice(0, -1).join(', ')} y ${days[days.length - 1]}`;
    return `${days.length === 1 ? 'El día' : 'Los días'} ${list}`;
};

const numberToSpanishWords = (value) => {
    const units = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
    const teens = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
    const tens = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
    const hundreds = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];
    const underThousand = (number) => {
        if (number === 0) return '';
        if (number === 100) return 'cien';
        if (number < 10) return units[number];
        if (number < 20) return teens[number - 10];
        if (number < 30) return number === 20 ? 'veinte' : `veinti${units[number - 20]}`;
        if (number < 100) return `${tens[Math.floor(number / 10)]}${number % 10 ? ` y ${units[number % 10]}` : ''}`;
        return `${hundreds[Math.floor(number / 100)]}${number % 100 ? ` ${underThousand(number % 100)}` : ''}`;
    };
    const integerWords = (number) => {
        if (number === 0) return 'cero';
        const millions = Math.floor(number / 1000000);
        const thousands = Math.floor((number % 1000000) / 1000);
        const remainder = number % 1000;
        const parts = [];
        if (millions) parts.push(millions === 1 ? 'un millón' : `${integerWords(millions)} millones`);
        if (thousands) parts.push(thousands === 1 ? 'mil' : `${underThousand(thousands)} mil`);
        if (remainder) parts.push(underThousand(remainder));
        return parts.join(' ');
    };
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const absoluteAmount = Math.abs(amount);
    const integer = Math.floor(absoluteAmount);
    const cents = Math.round((absoluteAmount - integer) * 100);
    const normalizedInteger = cents === 100 ? integer + 1 : integer;
    const normalizedCents = cents === 100 ? 0 : cents;
    const words = integerWords(normalizedInteger)
        .replace(/veintiuno$/, 'veintiún')
        .replace(/uno$/, 'un');
    return `${amount < 0 ? 'menos ' : ''}${words} pesos ${String(normalizedCents).padStart(2, '0')}/100 M.N.`;
};

const weeklyHours = (schedule = {}) => {
    const hours = (periods = {}) => {
        const period = (start, end) => {
            if (!start || !end) return 0;
            const [startHour, startMinute] = start.split(':').map(Number);
            const [endHour, endMinute] = end.split(':').map(Number);
            let value = endHour + endMinute / 60 - startHour - startMinute / 60;
            if (value < 0) value += 24;
            return value;
        };
        return period(periods.inicio, periods.fin) + period(periods.inicio2, periods.fin2);
    };
    const subtractBreak = Number(schedule.tiempoDescanso || 0) / 60;
    if (schedule.horarioFijo?.horas) {
        const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
        const start = days.findIndex((day) => normalize(day) === normalize(schedule.horarioFijo.desdeDia));
        const end = days.findIndex((day) => normalize(day) === normalize(schedule.horarioFijo.hastaDia));
        let workDays = Math.max(0, 7 - Number(schedule.diasDescanso || 0));
        if (start >= 0 && end >= 0) {
            workDays = 1;
            for (let index = start; index !== end;) {
                index = (index + 1) % days.length;
                workDays += 1;
            }
        }
        return Math.max(0, hours(schedule.horarioFijo.horas) - subtractBreak)
            * workDays;
    }
    if (Array.isArray(schedule.horarioVariable)) {
        return schedule.horarioVariable
            .filter((entry) => entry.dia)
            .reduce((total, entry) => total + Math.max(0, hours(entry.horas) - subtractBreak), 0);
    }
    return Number(schedule.horasSemanales) || 0;
};

const weeklyRestDays = (schedule = {}) => {
    if (['fijoDescansoVariable', 'variableDescansoVariable'].includes(schedule.tipoHorario)) {
        return 'VARIABLE Y SEGÚN ROL DE TURNOS';
    }
    const week = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    if (schedule.tipoHorario === 'fijoDiasFijos' && schedule.horarioFijo) {
        const start = week.findIndex((day) => normalize(day) === normalize(schedule.horarioFijo.desdeDia));
        const end = week.findIndex((day) => normalize(day) === normalize(schedule.horarioFijo.hastaDia));
        if (start < 0 || end < 0) return '';
        const workDays = [];
        for (let index = start, count = 0; count < week.length; count += 1) {
            workDays.push(week[index]);
            if (index === end) break;
            index = (index + 1) % week.length;
        }
        return week.filter((day) => !workDays.includes(day)).join(', ');
    }
    if (schedule.tipoHorario === 'variableDiasFijos' && Array.isArray(schedule.horarioVariable)) {
        const workDays = schedule.horarioVariable
            .map((row) => week.find((day) => normalize(day) === normalize(row.dia)))
            .filter(Boolean);
        return week.filter((day) => !workDays.includes(day)).join(', ');
    }
    return '';
};

const calculatorTotal = (data = {}) => {
    const calculator = data.calculator || {};
    if (calculator.total !== undefined && calculator.total !== null && calculator.total !== '') return calculator.total;
    if (calculator.total_percepciones !== undefined || calculator.total_deducciones !== undefined) {
        return Number(calculator.total_percepciones || 0) - Number(calculator.total_deducciones || 0);
    }
    return data.total ?? '';
};

const resolveMarker = (marker, document, company, collaborator, collaborators, context) => {
    const text = normalize(marker);
    const { data, isContract, schedule, additional, workerName } = context;
    const companyName = company?.nombre || '';
    const companyAddress = getCompanyAddress(company);
    const workplaceAddress = getWorkAddress(document, data);
    const companyRepresentative = getDataValue(company, ['representante_legal', 'representante'])
        || getDataValue(data, ['representante_legal', 'representante'])
        || getDataValue(additional, ['representante_legal', 'representante']);
    const salary = document.salario ?? getDataValue(data, ['salario', 'sueldo_diario_bruto']);
    const totalToPay = calculatorTotal(data);

    if (text.includes('CONCEPTOS ADICIONALES')) {
        return additionalBenefitsRows(document, additional).length
            ? additionalBenefitsGridToken
            : removeMarker;
    }
    if (text.includes('APARECER EN UNA TABLA') && text.includes('HORARIOS DIARIOS')) {
        return scheduleTableRows(schedule).length ? scheduleGridToken : '';
    }
    if (text.includes('REPRESENTANTE') && (text.includes('TRABAJADOR') || text.includes('OBRERO'))) {
        const index = text.includes('2') || text.includes('SEGUNDO') ? 'trabajador2' : 'trabajador';
        const selected = collaborators.find((person) => String(person.id) === String(data[index]));
        return getFullName(selected);
    }
    if (text.includes('NOMBRE') && text.includes('REP') && text.includes('PATRON')) {
        return companyRepresentative;
    }
    if (text.includes('REPRESENTANTE PATRONAL') || text.includes('REPRESENTANTE DEL PATRON') || text.includes('REP. DEL PATRON')) {
        const key = text.includes('2') || text.includes('SEGUNDO') ? 'rep_patron2' : 'rep_patron';
        const value = getDataValue(data, [key]);
        return value || (key === 'rep_patron' ? companyRepresentative : '');
    }
    if (text.includes('REPRESENTANTE LEGAL')) {
        return getDataValue(data, ['representante_legal', 'representante']) || companyRepresentative;
    }
    if (text.includes('REPRESENTANTE') && !text.includes('TRABAJADOR') && !text.includes('OBRERO')) {
        return getDataValue(data, ['representante_legal', 'representante'])
            || getDataValue(additional, ['representante_legal', 'representante'])
            || companyRepresentative;
    }
    if (
        (text.includes('NOMBRE') && (text.includes('PATRON') || text.includes('EMPRESA') || text.includes('RAZON SOCIAL')))
        || (text.includes('RAZON SOCIAL') && text.includes('PATRON'))
    ) {
        return companyName;
    }
    if (text.includes('NOMBRE') && (text.includes('TRABAJADOR') || text.includes('COLABORADOR') || text.includes('EMPLEADO'))) {
        return workerName || getDataValue(data, ['nombre']);
    }
    if (text.includes('DOMICILIO FISCAL')) return companyAddress;
    if (text.includes('DOMICILIO') && (text.includes('SUCURSAL') || text.includes('CENTRO DE TRABAJO'))) {
        return workplaceAddress;
    }
    if (text.includes('DOMICILIO') && (text.includes('PATRON') || text.includes('EMPRESA'))) {
        return getDataValue(data, ['domicilio_patron', 'lugar_trabajo']) || companyAddress;
    }
    if (text.includes('DOMICILIO') && (text.includes('PARTICULAR') || text.includes('TRABAJADOR'))) {
        return collaborator?.domicilio || getDataValue(data, ['domicilio_colaborador', 'domicilio_trabajador']);
    }
    if (text.includes('ANO DE INICIO DE ACTIVIDADES')) {
        const startDate = getDataValue(data, ['inicio', 'fecha_inicio_actividades']);
        return startDate ? String(startDate).slice(0, 4) : '';
    }
    if (isContract && text.includes('SERVICIOS QUE PRESTARA EL COLABORADOR')) {
        return getDataValue(document, ['actividad']);
    }
    if (text.includes('OBJETO SOCIAL') || text.includes('ACTIVIDAD') || text.includes('GIRO')) {
        return getDataValue(data, ['giro_actividad', 'giro', 'actividad']) || company?.giro_actividad || '';
    }
    if (text.includes('RFC') && (text.includes('PATRON') || text.includes('EMPRESA'))) return company?.rfc || data.rfc || '';
    if (
        text.includes('RFC')
        && (text.includes('TRABAJADOR') || text.includes('COLABORADOR') || text.includes('EMPLEADO'))
    ) {
        return collaborator?.rfc || '';
    }
    if (text.includes('CURP')) return collaborator?.curp || '';
    if (text.includes('SEGURIDAD SOCIAL') || text.includes('NSS')) return collaborator?.nss || '';
    if (text.includes('NACIONALIDAD')) return collaborator?.nacionalidad || '';
    if (text.includes('ESTADO CIVIL')) return collaborator?.estado_civil || '';
    if (text.includes('SEXO')) return collaborator?.sexo || '';
    if (text.includes('EDAD')) {
        const birthDate = collaborator?.fecha_nacimiento;
        return birthDate ? Math.floor((Date.now() - new Date(birthDate).getTime()) / 31557600000) : '';
    }
    if (text.includes('PUESTO')) return getDataValue(data, ['puesto']) || document.puesto || additional.puesto || '';
    if (text.includes('CORREO') || text.includes('EMAIL')) {
        if (text.includes('PATRON') || text.includes('EMPRESA') || text.includes('PATRONAL')) {
            return getDataValue(data, ['email_empresa', 'correo_empresa', 'email', 'correo'])
                || company?.email
                || company?.correo
                || '';
        }
        if (text.includes('TRABAJADOR') || text.includes('COLABORADOR')) {
            return collaborator?.email || getDataValue(data, ['email', 'correo']);
        }
        return getDataValue(data, ['email', 'correo']) || collaborator?.email || '';
    }
    if (text.includes('LUGAR DE FIRMA') || text.includes('LUGAR Y FECHA DE PAGO')) {
        const place = getDataValue(data, ['lugar_pago', 'lugar_ciudad', 'ciudad'])
            || getDataValue(document, ['lugar_ciudad']);
        const state = getDataValue(data, ['lugar_estado', 'estado'])
            || getDataValue(document, ['lugar_estado']);
        const date = getDataValue(data, ['fecha_pago', 'fecha_ultimo_dia', 'fecha'])
            || getDataValue(document, ['fecha_celebracion']);
        return [place, state, date ? formatDate(date) : ''].filter(Boolean).join(', ');
    }
    if (text.includes('CARGA HORARIA SEMANAL') || text.includes('HORAS SEMANALES')) {
        const hours = weeklyHours(schedule);
        return hours ? String(Number(hours.toFixed(2))) : '';
    }
    if (text.includes('CONTINUA O DISCONTINUA')) {
        return {
            continua: 'Continua',
            discontinua: 'Discontinua'
        }[schedule.tipoJornada] || schedule.tipoJornada || '';
    }
    if (text.includes('TIPO DE CONTRATO')) {
        return document.tipo === 'obra' || document.tipo === 'determinado'
            ? 'determinado'
            : 'indeterminado';
    }
    if (text.includes('DIAS DE DESCANSO') && text.includes('SEMANA')) return schedule.diasDescanso ?? '';
    if (text.includes('DIAS ELEGIDOS DE DESCANSO')) return weeklyRestDays(schedule);
    if (text.includes('HORARIO') || text.includes('JORNADA')) return formatSchedule(schedule);
    if (text.includes('SALARIO EN LETRA') || (text.includes('SALARIO') && text.includes('LETRA'))) {
        return getDataValue(data, ['salario_letra', 'salario_texto'])
            || getDataValue(additional, ['salario_letra', 'salario_texto'])
            || numberToSpanishWords(salary);
    }
    if (text.includes('TOTAL A PAGAR EN LETRA')) return numberToSpanishWords(totalToPay);
    if (text.includes('TOTAL A PAGAR') && !text.includes('LETRA')) return totalToPay;
    if (text.includes('FRECUENCIA') && text.includes('PAGO')) return document.periodo_pago || '';
    if (text.includes('ANIVERSARIO')) {
        const anniversary = getDataValue(data, ['aniversario'])
            || getDataValue(document, ['aniversario'])
            || getDataValue(additional, ['aniversario']);
        const hireDate = getDataValue(data, ['alta', 'fecha_ingreso'])
            || getDataValue(document, ['alta', 'fecha_ingreso'])
            || collaborator?.alta
            || collaborator?.fecha_ingreso;
        const hireYear = hireDate
            ? new Date(`${String(hireDate).slice(0, 10)}T00:00:00`).getFullYear()
            : 0;
        const selectedYear = Number(anniversary);
        return hireYear && selectedYear >= hireYear
            ? selectedYear - hireYear
            : anniversary;
    }

    const mappings = [
        [/FECHA.*ULTIMO DIA LABORADO|ULTIMO DIA LABORADO|FECHA.*TERMINO|FECHA.*FIN|FIN.*VACACIONES/, ['fecha_ultimo_dia', 'fecha_fin', 'fin']],
        [/FECHA.*REUNION|FECHA DE CELEBRACION|FECHA DE ELABORACION|FECHA DE AUTORIZACION|FECHA DE SOLICITUD|FECHA.*CONSTANCIA/, ['fecha', 'elaboracion', 'recepcion']],
        [/FECHA.*INGRESO|FECHA.*ALTA/, ['alta', 'fecha_ingreso', 'inicio']],
        [/FECHA.*INICIO|INICIO.*LICENCIA|INICIO.*VACACIONES/, ['fecha_inicio', 'inicio']],
        [/FECHA.*REGRESO|FECHA.*LABORES/, ['fecha_labores']],
        [/FECHA.*PAGO/, ['fecha_pago']],
        [/FECHA.*DECLARACION|PRESENTACION DE DECLARACION/, ['declaracion', 'fecha_declaracion']],
        [/CIUDAD|MUNICIPIO/, ['ciudad']],
        [/ESTADO/, ['estado']],
        [/HORA.*CIERRE|CIERRE.*ACTA|HORA.*CEIRRE/, ['hora_cierre']],
        [/HORA.*INICIO|INICIO.*ACTA/, ['hora_inicio', 'inicio']],
        [/EJERCICIO FISCAL|EJERCICIO/, ['ejercicio']],
        [/NUMERO.*DIAS|DIAS.*VACACIONES|DIAS A GOZAR|DIAS AUTORIZADOS/, ['dias']],
        [/MONTO.*NUMERO|MONTO.*ANTICIPO/, ['monto']],
        [/MONTO.*LETRA/, ['letra']],
        [/REPRESENTANTE/, ['representante_legal', 'representante']],
        [/LUGAR.*PAGO/, ['lugar_pago']],
        [/PUESTO/, ['puesto']],
        [/AREA|GIRO/, ['giro', 'giro_actividad']],
        [/REGISTRO PATRONAL/, ['registro_patronal']],
        [/TELEFONO/, ['telefono']],
        [/CORREO|EMAIL/, ['email']],
        [/EMPLEADOS|TRABAJADORES VIGENTES/, ['empleados']],
        [/TURNOS/, ['turnos']],
        [/DIA.*RECIBE|DIA DE RECEPCION/, ['dia', 'recepcion']],
        [/MES.*RECIBE|MES DE RECEPCION/, ['mes', 'recepcion']],
        [/DURACION.*CONTRATO|DURACION.*TIEMPO/, ['duracion']],
        [/DURACION.*CAPACITACION/, ['duracion_capacitacion', 'prueba']],
        [/DURACION.*PRUEBA/, ['duracion_prueba', 'prueba']],
        [/DURACION.*TEMPORADA|ESPACIO TEMPORAL/, ['duracion_temporada', 'temporada']],
        [/MOTIVO.*TIEMPO DETERMINADO|RAZON.*MOTIVO/, ['motivo', 'razon']],
        [/CLIENTE O PROYECTO/, ['cliente', 'proyecto']],
        [/DESCRIPCION.*OBRA|DESCRIPCION.*PROYECTO/, ['descripcion_obra', 'proyecto']],
        [/TEMPORALIDAD DE PAGO|PERIODICIDAD.*PAGO/, ['periodo_pago', 'periodo']],
        [/DIA.*PAGAR.*SALARIO|DIAS.*PAGO/, ['dias_pago', 'pagos']],
        [/FECHA.*CONTRATACION|FECHA.*CONTRATO/, ['fecha_celebracion', 'fecha_contratacion']],
        [/DURACION|TEMPORALIDAD|RAZON.*TEMPORADA/, ['duracion', 'duracion_temporada', 'razon_temporada']]
    ];
    for (const [pattern, keys] of mappings) {
        if (!pattern.test(text)) continue;
        let value = getDataValue(data, keys)
            || (isContract ? getDataValue(document, keys) : '')
            || (isContract ? getDataValue(additional, keys) : '');
        if (keys.includes('dias_pago')) value = formatPaymentDays(value);
        if (value !== '') {
            return /FECHA|INICIO|FIN|ALTA|RECEPCION|DECLARACION|CONTRATACION/.test(text) ? formatDate(value) : value;
        }
    }
    if (text.includes('SALARIO')) return salary;
    return '';
};

const paragraphText = (paragraph) => (paragraph.t || []).map((run) => run.t || '').join('');

const trimMarkerFill = (text, start, end) => {
    const filler = /[_\-\u2013\u2014]/;
    let fillStart = start;
    while (fillStart > 0 && filler.test(text[fillStart - 1])) fillStart -= 1;

    let fillEnd = end;
    while (fillEnd < text.length && filler.test(text[fillEnd])) fillEnd += 1;

    const surroundingFill = text.slice(fillStart, start) + text.slice(end, fillEnd);
    return /_/.test(surroundingFill) || /[-\u2013\u2014]{2,}/.test(surroundingFill)
        ? [fillStart, fillEnd]
        : [start, end];
};

const renderRuns = (paragraph, replaceMarker, preserveSignatureFill) => {
    const runs = paragraph.t || [];
    const fullText = paragraphText(paragraph);
    const renderRange = (start, end) => {
        let offset = 0;
        return runs.map((run) => {
            const runStart = offset;
            const runEnd = offset + (run.t || '').length;
            offset = runEnd;
            const from = Math.max(start, runStart);
            const to = Math.min(end, runEnd);
            if (from >= to) return '';
            const text = escapeHtml(run.t.slice(from - runStart, to - runStart));
            if (!run.b && !run.i && !run.u) return text;
            const tags = `${run.b ? '<b>' : ''}${run.i ? '<i>' : ''}${run.u ? '<u>' : ''}`;
            const closes = `${run.u ? '</u>' : ''}${run.i ? '</i>' : ''}${run.b ? '</b>' : ''}`;
            return `${tags}${text}${closes}`;
        }).join('');
    };

    const markerPattern = /\(?NOMBRE DEL\(A\) TRABAJADOR\(A\)\)?|\(((?:[^()]|\([^()]*\))*)\)|AQU[IÍ]\s+(?:DEBE|DEBER[AÁ]N?|APARECER|PRECISARSE)(?=\s)[^.!?]*[.!?]?|Representante Legal/gi;
    let result = '';
    let position = 0;
    let match;
    while ((match = markerPattern.exec(fullText)) !== null) {
        const replacement = replaceMarker(match[1] || match[0]);
        if (replacement === removeMarker) {
            const [start, end] = preserveSignatureFill
                ? [match.index, markerPattern.lastIndex]
                : trimMarkerFill(fullText, match.index, markerPattern.lastIndex);
            result += renderRange(position, start);
            position = end;
            continue;
        }
        if (replacement === scheduleGridToken || replacement === additionalBenefitsGridToken) {
            result += renderRange(position, match.index) + replacement;
            position = markerPattern.lastIndex;
            continue;
        }
        if (replacement === '' || replacement === null || replacement === undefined) continue;
        const signatureMarker = normalize(match[1]).includes('FIRMA');
        const representativeSignatureMarker = normalize(match[1] || match[0]).includes('REP')
            && normalize(match[1] || match[0]).includes('PATRON');
        const [start, end] = (preserveSignatureFill && !representativeSignatureMarker) || signatureMarker
            ? [match.index, markerPattern.lastIndex]
            : trimMarkerFill(fullText, match.index, markerPattern.lastIndex);
        const replacementText = String(replacement);
        const previousCharacter = fullText[start - 1] || '';
        const nextCharacter = fullText[end] || '';
        const prefixSpace = !preserveSignatureFill && !signatureMarker
            && previousCharacter && !/\s|[(\[{]/.test(previousCharacter)
            && /^\w/.test(replacementText)
            ? ' '
            : '';
        const suffixSpace = !preserveSignatureFill && !signatureMarker
            && nextCharacter && !/\s|[.,;:!?)}\]]/.test(nextCharacter)
            && /\w$/.test(replacementText)
            ? ' '
            : '';
        result += renderRange(position, start) + prefixSpace + escapeHtml(replacementText) + suffixSpace;
        position = end;
    }
    return result + renderRange(position, fullText.length);
};

const paragraphStyle = (paragraph) => {
    const styles = ['margin:0 0 8pt'];
    const alignments = { both: 'justify', center: 'center', right: 'right', left: 'left' };
    if (alignments[paragraph.a]) styles.push(`text-align:${alignments[paragraph.a]}`);
    if (paragraph.b !== undefined) styles.push(`margin-top:${Number(paragraph.b) / 20}pt`);
    if (paragraph.d !== undefined) styles.push(`margin-bottom:${Number(paragraph.d) / 20}pt`);
    if (paragraph.s === 'Prrafodelista') styles.push('padding-left:20pt');
    return styles.join(';');
};

const renderParagraph = (paragraph, replaceMarker, preserveSignatureFill, schedule, document, additional) => {
    const fullText = paragraphText(paragraph);
    if (!document.isLegalDocument && normalize(fullText.trim()) === 'VACACIONES') {
        return `<p style="${paragraphStyle(paragraph)}">${escapeHtml(fullText)}</p>${renderVacationTable(document.vacaciones)}`;
    }
    const text = renderRuns(paragraph, replaceMarker, preserveSignatureFill);
    if (text.includes(scheduleGridToken) || text.includes(additionalBenefitsGridToken)) {
        const gridToken = text.includes(scheduleGridToken) ? scheduleGridToken : additionalBenefitsGridToken;
        const [before, after] = text.split(gridToken);
        const grid = gridToken === scheduleGridToken
            ? renderScheduleGrid(schedule)
            : renderAdditionalBenefitsGrid(document, additional);
        return `${before ? `<p style="${paragraphStyle(paragraph)}">${before}</p>` : ''}${grid}${after ? `<p style="${paragraphStyle(paragraph)}">${after}</p>` : ''}`;
    }
    return `<p style="${paragraphStyle(paragraph)}">${text || '&nbsp;'}</p>`;
};

const signatureSectionStart = /\b(FIRMANDO|FIRMAN AL CALCE|FIRMAN LOS|ATENTAMENTE|REPRESENTANTES POR LA EMPRESA|REPRESENTANTES POR LOS TRABAJADORES)\b/;

const renderBlocks = (source, replaceMarker, schedule = {}, document = {}, additional = {}) => {
    let signatureSection = false;
    return source.blocks.map((block, index) => {
        if (block.p) {
            const text = paragraphText(block.p);
            const nextParagraph = source.blocks.slice(index + 1)
                .find((nextBlock) => nextBlock.p && paragraphText(nextBlock.p).trim());
            if (
                /^[\s_\-\u2013\u2014]+$/.test(text)
                && nextParagraph
                && /^DECLARA\s+.*TRABAJADOR/.test(normalize(paragraphText(nextParagraph.p).trim()))
            ) {
                return '';
            }
            const rendered = renderParagraph(block.p, replaceMarker, signatureSection, schedule, document, additional);
            if (signatureSectionStart.test(normalize(text))) signatureSection = true;
            return rendered;
        }
        if (block.table) {
            const rows = block.table.map((row) => `<tr>${row.map((cell) =>
                `<td>${renderBlocks({ blocks: cell }, replaceMarker, schedule, document, additional)}</td>`
            ).join('')}</tr>`).join('');
            return `<table>${rows}</table>`;
        }
        return '';
    }).join('');
};

export const buildSourceDocumentTemplate = (document, company, collaborator, collaborators = []) => {
    const source = findSourceTemplate(document);
    if (!source) return null;
    const isContract = !document.isLegalDocument;
    const data = document.data || {};
    const additional = document.additionalFields || {};
    const schedule = parseObject(document.jornada_laboral);
    const workerName = getFullName(collaborator) || data.nombre || document.nombre || '';
    const context = { data, isContract, additional, schedule, workerName };
    const replaceMarker = (marker) => resolveMarker(
        marker,
        document,
        company,
        collaborator,
        collaborators,
        context
    );

    return `<div id="document-template" class="source-document-template">
        <style>
            .source-document-template{width:210mm;min-height:297mm;padding:8mm 24mm 12mm;background:#fff;color:#111;box-sizing:border-box;font-family:"Book Antiqua",serif;font-size:10pt;line-height:1.25}
            .source-document-template p{white-space:pre-wrap;overflow-wrap:break-word}
            .source-document-template .contract-data-grid{display:block;width:100%;margin:10pt 0;font-size:9pt;page-break-inside:avoid}
            .source-document-template .contract-grid-row{display:grid;grid-template-columns:22% 48% 30%;width:100%;break-inside:avoid}
            .source-document-template .contract-grid-cell{box-sizing:border-box;min-width:0;padding:6pt 7pt;border:1px solid #555;overflow-wrap:anywhere;white-space:normal}
            .source-document-template .contract-grid-row+.contract-grid-row .contract-grid-cell{border-top:0}
            .source-document-template .contract-grid-heading{background:#e8edf3;font-weight:bold}
            .source-document-template .contract-grid-row:nth-child(even){background:#f6f8fa}
            .source-document-template .contract-benefits-grid .contract-grid-row{grid-template-columns:44% 28% 28%}
            .source-document-template .contract-vacation-grid .contract-grid-row{grid-template-columns:50% 50%}
            .source-document-template .contract-schedule-summary{display:grid;grid-template-columns:30% 70%;width:100%;margin:10pt 0 0;font-size:9pt;page-break-inside:avoid}
            .source-document-template .contract-schedule-summary-label,.source-document-template .contract-schedule-summary-value{box-sizing:border-box;padding:5pt 7pt;border:1px solid #555;overflow-wrap:anywhere}
            .source-document-template .contract-schedule-summary-label{font-weight:bold;background:#e8edf3}
            .source-document-template .contract-schedule-summary-value{border-left:0}
            .source-document-template .contract-schedule-grid .contract-grid-row{grid-template-columns:22% 48% 30%}
        </style>
        ${renderBlocks(source, replaceMarker, schedule, document, additional)}
    </div>`;
};
